import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Card from 'react-bootstrap/Card'
import Form from 'react-bootstrap/Form'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Table from 'react-bootstrap/Table'
import Tooltip from 'react-bootstrap/Tooltip'
import ApplyPanel from './ApplyPanel'
import Pagination from '../../../component/Pagination'
import useDecisions from './useDecisions'

// Phantom courses: ordinary-looking names created only to get a scorecard.
//
// The server scores each course by how many phantom traits it shows (no location, all
// par 3, one layout, never played, …) but cannot tell a phantom from a real course with
// a thin record. You can: search the name, and record what you found. The verdicts are
// stored server-side, so a session can stop anywhere and pick up where it left off.
//
// Two separate steps, on purpose:
// 1. Review — a verdict per course (Not found / Found / Unsure). Writes only the review
//    table; the course itself is untouched.
// 2. Hide — through the ordinary hides apply, with its dry run, staleness check and audit.
//    Never pre-decided: a Not found verdict fills the decision only when you ask it to.

export const STATUS_FILTERS = [
  { key: 'UNREVIEWED', label: 'Unreviewed' },
  { key: 'NOT_FOUND', label: 'Not found' },
  { key: 'UNSURE', label: 'Unsure' },
  { key: 'FOUND', label: 'Found' },
]

const VERDICT_BADGE = {
  NOT_FOUND: { bg: 'danger', label: 'Not found' },
  FOUND: { bg: 'success', label: 'Found' },
  UNSURE: { bg: 'warning', label: 'Unsure' },
}

const SCORE_BG = {
  9: 'danger',
  8: 'danger',
  7: 'danger',
  6: 'danger',
  5: 'warning',
  4: 'info',
  3: 'secondary',
}

const SIGNAL_LABELS = {
  SUSPECT_NAME: 'name',
  NO_LOCATION: 'no location',
  ALL_PAR_3: 'all par 3',
  NO_INFO: 'no info',
  SINGLE_LAYOUT: '1 layout',
  NEVER_PLAYED: 'never played',
  NOT_A_PLACE: 'not a place',
  PLAYER_NAME: 'player name',
  HOME_SPOT: 'home spot',
}

const MIN_SCORES = [3, 4, 5, 6, 7, 8, 9]

const distance = (metres) =>
  metres < 1000 ? `${metres} m` : `${(metres / 1000).toFixed(1)} km`

// The place the server matched the name to, in words. A near match is the course's own
// hill or district; "≈" marks a clipped one ("Kaatis" for Kaatiskallio), which is how
// slang for a course usually looks. Somewhere else in the country proves much less.
export const placeText = (place, countryName) => {
  if (!place) return null
  const where =
    place.kind === 'IN_COUNTRY'
      ? `elsewhere in ${countryName || 'the country'}`
      : distance(place.metres ?? 0)
  const name = place.kind === 'NEAR_PREFIX' ? `≈ ${place.name}` : place.name
  return `${name} · ${place.featureLabel} · ${where}`
}

// The search you would type yourself. Quoting the name keeps Google from "correcting"
// a made-up word into a real one, which is exactly the result that would mislead.
export const searchUrl = (row, countryName) => {
  const place = [row.googleRegion, countryName].filter(Boolean).join(' ')
  const query = `"${row.name}" discgolf ${place}`.trim()
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`
}

// The map, for the course whose name is not on it: is there a park or a hill under
// another spelling nearby?
export const mapUrl = (row, countryName) => {
  const query = [row.name, row.googleRegion, countryName]
    .filter(Boolean)
    .join(', ')
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    query
  )}`
}

// One named window, reused for every search. Opening a tab per course would bury the
// page, and every call follows a click or a keypress so popup blockers let it through.
const openSearch = (row, countryName) =>
  window.open(searchUrl(row, countryName), 'phantom-search')

const openMap = (row, countryName) =>
  window.open(mapUrl(row, countryName), 'phantom-search')

const isTyping = (event) => {
  const tag = event.target?.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    event.target?.isContentEditable
  )
}

export default function PhantomsPass({
  country,
  countryName,
  data,
  applying,
  dryRunSeen,
  onDryRun,
  onApply,
  onReview,
}) {
  const [page, setPage] = useState(0)
  const [status, setStatus] = useState('UNREVIEWED')
  const [minScore, setMinScore] = useState(5)
  const [requiredSignals, setRequiredSignals] = useState([])
  const [selected, setSelected] = useState(() => new Set())
  // Verdicts recorded this session, laid over the ones the server sent, so a review
  // shows at once instead of waiting for a recompute of the whole country.
  const [localReviews, setLocalReviews] = useState({})
  const [reviewError, setReviewError] = useState(null)
  // A snapshot of the rows being stepped through. Snapshotted rather than derived, because
  // with the Unreviewed filter on, each verdict removes its row from the table.
  const [queue, setQueue] = useState(null)
  const [position, setPosition] = useState(0)
  const [autoOpen, setAutoOpen] = useState(true)
  const { decisions, setDecision, decidedCount, clear } = useDecisions(
    'phantoms',
    country
  )

  // A different country is a different list; nothing selected or queued carries over.
  useEffect(() => {
    setSelected(new Set())
    setQueue(null)
    setLocalReviews({})
    setPage(0)
  }, [country])

  const candidates = useMemo(() => data?.candidates || [], [data])
  const signals = useMemo(() => data?.signals || [], [data])

  const verdictOf = useCallback(
    (row) => localReviews[row.courseId] ?? row.review?.verdict ?? null,
    [localReviews]
  )

  const rows = useMemo(
    () =>
      candidates.filter((row) => {
        const verdict = verdictOf(row)
        if (status === 'UNREVIEWED' ? verdict !== null : verdict !== status)
          return false
        if (row.score < minScore) return false
        return requiredSignals.every((s) => row.signals.includes(s))
      }),
    [candidates, verdictOf, status, minScore, requiredSignals]
  )

  // Optimistic: the verdict shows at once and is taken back if the server refuses it.
  const record = useCallback(
    (targets, verdict) => {
      if (targets.length === 0) return Promise.resolve()
      const ids = targets.map((row) => row.courseId)
      const forget = (dropIds) =>
        setLocalReviews((currentReviews) => {
          const next = { ...currentReviews }
          dropIds.forEach((id) => delete next[id])
          return next
        })

      setReviewError(null)
      setLocalReviews((currentReviews) => {
        const next = { ...currentReviews }
        ids.forEach((id) => (next[id] = verdict))
        return next
      })
      const payload = targets.map((row) => ({
        courseId: row.courseId,
        name: row.name,
        verdict,
      }))
      return Promise.resolve(onReview(payload))
        .then((report) => {
          const refused = (report?.outcomes || []).filter(
            (o) => o.status !== 'APPLIED'
          )
          if (refused.length > 0) {
            forget(refused.map((o) => o.entityId))
            setReviewError(refused.map((o) => o.reason).join('; '))
          }
        })
        .catch(() => {
          forget(ids)
          setReviewError('Could not save the verdict. Nothing was recorded.')
        })
    },
    [onReview]
  )

  const current = queue ? queue[position] : null

  const step = useCallback(
    (delta) => {
      if (!queue) return
      const next = position + delta
      if (next < 0) return
      if (next >= queue.length) {
        setQueue(null)
        return
      }
      setPosition(next)
      if (delta > 0 && autoOpen) openSearch(queue[next], countryName)
    },
    [queue, position, autoOpen, countryName]
  )

  const decide = useCallback(
    (verdict) => {
      if (!current) return
      record([current], verdict)
      step(1)
    },
    [current, record, step]
  )

  useEffect(() => {
    if (!current) return undefined
    const onKey = (event) => {
      if (isTyping(event) || event.ctrlKey || event.metaKey || event.altKey)
        return
      const key = event.key.toLowerCase()
      const actions = {
        g: () => openSearch(current, countryName),
        enter: () => openSearch(current, countryName),
        m: () => openMap(current, countryName),
        n: () => decide('NOT_FOUND'),
        f: () => decide('FOUND'),
        u: () => decide('UNSURE'),
        s: () => step(1),
        arrowright: () => step(1),
        arrowleft: () => step(-1),
        escape: () => setQueue(null),
      }
      if (actions[key]) {
        event.preventDefault()
        actions[key]()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [current, countryName, decide, step])

  if (!data) return null

  const selectedRows = candidates.filter((row) => selected.has(row.courseId))

  const toggle = (courseId) =>
    setSelected((currentSet) => {
      const next = new Set(currentSet)
      if (next.has(courseId)) next.delete(courseId)
      else next.add(courseId)
      return next
    })

  const setAll = (pageRows, on) =>
    setSelected((currentSet) => {
      const next = new Set(currentSet)
      pageRows.forEach((row) =>
        on ? next.add(row.courseId) : next.delete(row.courseId)
      )
      return next
    })

  const startReview = () => {
    // Table order, not click order, so the walk matches what is on screen.
    const ordered = rows.filter((row) => selected.has(row.courseId))
    const walk = ordered.length > 0 ? ordered : selectedRows
    if (walk.length === 0) return
    setQueue(walk)
    setPosition(0)
    openSearch(walk[0], countryName)
  }

  const markSelected = (verdict) => record(selectedRows, verdict)

  const hideNotFound = () =>
    selectedRows
      .filter((row) => verdictOf(row) === 'NOT_FOUND')
      .forEach((row) => setDecision(row.courseId, 'HIDE'))

  const toggleSignal = (name) =>
    setRequiredSignals((currentSignals) =>
      currentSignals.includes(name)
        ? currentSignals.filter((s) => s !== name)
        : [...currentSignals, name]
    )

  const submit = (dryRun) => {
    const payload = Object.entries(decisions)
      .filter(([, action]) => action === 'HIDE')
      .map(([courseId, action]) => {
        const row = candidates.find(
          (c) => String(c.courseId) === String(courseId)
        )
        return {
          courseId: Number(courseId),
          oldName: row ? row.name : '',
          action,
        }
      })
    ;(dryRun ? onDryRun : onApply)(payload, dryRun ? undefined : clear)
  }

  const notFoundSelected = selectedRows.filter(
    (row) => verdictOf(row) === 'NOT_FOUND'
  ).length

  return (
    <>
      <Alert variant="light" className="small">
        Courses scored by how many phantom traits they show. Select some, press{' '}
        <strong>Review selected</strong>, and step through them: the Google
        search opens in a separate window, then <kbd>N</kbd> not found,{' '}
        <kbd>F</kbd> found, <kbd>U</kbd> unsure, <kbd>S</kbd>/<kbd>→</kbd> skip,{' '}
        <kbd>←</kbd> back, <kbd>G</kbd> search again, <kbd>M</kbd> map,{' '}
        <kbd>Esc</kbd> stop. Verdicts are saved as you go. Hiding is a separate
        step below.
      </Alert>

      {reviewError && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setReviewError(null)}
        >
          {reviewError}
        </Alert>
      )}

      {current && (
        <Card
          className="mb-3 border-primary sticky-top phantom-review"
          data-testid="phantom-review"
        >
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <div className="text-muted small">
                  {position + 1} of {queue.length}
                </div>
                <h4 className="mb-1">
                  {current.name}
                  {current.secondaryName && (
                    <span className="text-muted fs-6">
                      {' '}
                      · {current.secondaryName}
                    </span>
                  )}
                </h4>
                <div className="small">
                  Score {current.score} · {current.holeCount} holes · #
                  {current.courseId}
                  {verdictOf(current) && (
                    <Badge
                      bg={VERDICT_BADGE[verdictOf(current)].bg}
                      className="ms-2"
                    >
                      {VERDICT_BADGE[verdictOf(current)].label}
                    </Badge>
                  )}
                </div>
                <div className="small text-muted" data-testid="phantom-place">
                  {placeText(current.place, countryName) ||
                    (data.placeAttribution ? 'Not on the map' : null)}
                </div>
              </div>
              <Form.Check
                type="switch"
                id="phantom-auto-open"
                label="Open next search automatically"
                checked={autoOpen}
                onChange={(e) => setAutoOpen(e.target.checked)}
              />
            </div>
            <div className="d-flex flex-wrap gap-2 mt-2">
              <Button
                variant="outline-primary"
                onClick={() => openSearch(current, countryName)}
              >
                Search (G)
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => openMap(current, countryName)}
              >
                Map (M)
              </Button>
              <Button variant="danger" onClick={() => decide('NOT_FOUND')}>
                Not found (N)
              </Button>
              <Button variant="success" onClick={() => decide('FOUND')}>
                Found (F)
              </Button>
              <Button variant="warning" onClick={() => decide('UNSURE')}>
                Unsure (U)
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => step(-1)}
                disabled={position === 0}
              >
                ← Back
              </Button>
              <Button variant="outline-secondary" onClick={() => step(1)}>
                Skip →
              </Button>
              <Button variant="link" onClick={() => setQueue(null)}>
                Stop (Esc)
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
        <ButtonGroup size="sm">
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.key}
              variant={status === f.key ? 'primary' : 'outline-primary'}
              onClick={() => {
                setStatus(f.key)
                setPage(0)
              }}
            >
              {f.label}
            </Button>
          ))}
        </ButtonGroup>

        <Form.Group className="d-flex align-items-center gap-2">
          <Form.Label htmlFor="phantom-min-score" className="mb-0 small">
            Min score
          </Form.Label>
          <Form.Select
            id="phantom-min-score"
            size="sm"
            style={{ width: 'auto' }}
            value={minScore}
            onChange={(e) => {
              setMinScore(Number(e.target.value))
              setPage(0)
            }}
          >
            {MIN_SCORES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <div className="d-flex flex-wrap gap-1 small align-items-center">
          <span className="text-muted">Must have:</span>
          {signals.map((s) => (
            <Button
              key={s.name}
              size="sm"
              title={s.description}
              variant={
                requiredSignals.includes(s.name) ? 'dark' : 'outline-dark'
              }
              onClick={() => {
                toggleSignal(s.name)
                setPage(0)
              }}
            >
              {SIGNAL_LABELS[s.name] || s.name}
            </Button>
          ))}
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <span className="small">
          <strong>{rows.length}</strong> shown of {candidates.length} ·{' '}
          <strong>{selected.size}</strong> selected
          {data.withheldAsCatalogueShortName > 0 && (
            <span className="text-muted" data-testid="phantom-withheld">
              {' '}
              · {data.withheldAsCatalogueShortName} withheld as the short name
              of a DiscGolfAPI course nearby ({data.catalogueAttribution})
            </span>
          )}
        </span>
        <Button size="sm" onClick={startReview} disabled={selected.size === 0}>
          Review selected
        </Button>
        <ButtonGroup size="sm">
          <Button
            variant="outline-danger"
            disabled={selected.size === 0}
            onClick={() => markSelected('NOT_FOUND')}
          >
            Mark not found
          </Button>
          <Button
            variant="outline-success"
            disabled={selected.size === 0}
            onClick={() => markSelected('FOUND')}
          >
            Mark found
          </Button>
          <Button
            variant="outline-warning"
            disabled={selected.size === 0}
            onClick={() => markSelected('UNSURE')}
          >
            Mark unsure
          </Button>
        </ButtonGroup>
        <Button
          size="sm"
          variant="danger"
          disabled={notFoundSelected === 0}
          onClick={hideNotFound}
        >
          Hide selected not found ({notFoundSelected})
        </Button>
        {selected.size > 0 && (
          <Button
            size="sm"
            variant="link"
            onClick={() => setSelected(new Set())}
          >
            Clear selection
          </Button>
        )}
      </div>

      <Pagination items={rows} page={page} onPage={setPage}>
        {(pageRows) => {
          const allOnPage =
            pageRows.length > 0 &&
            pageRows.every((row) => selected.has(row.courseId))
          return (
            <Table size="sm" responsive hover className="curation-table">
              <thead>
                <tr>
                  <th>
                    <Form.Check
                      aria-label="Select all on this page"
                      checked={allOnPage}
                      onChange={(e) => setAll(pageRows, e.target.checked)}
                    />
                  </th>
                  <th>Id</th>
                  <th>Name</th>
                  <th>Score</th>
                  <th>Signals</th>
                  <th>Place</th>
                  <th>Layouts</th>
                  <th>Holes</th>
                  <th>Played</th>
                  <th>Review</th>
                  <th>Search</th>
                  <th>Decision</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => {
                  const verdict = verdictOf(row)
                  const decision = decisions[row.courseId]
                  return (
                    <tr
                      key={row.courseId}
                      className={
                        current?.courseId === row.courseId
                          ? 'table-primary'
                          : undefined
                      }
                    >
                      <td>
                        <Form.Check
                          aria-label={`Select ${row.name}`}
                          checked={selected.has(row.courseId)}
                          onChange={() => toggle(row.courseId)}
                        />
                      </td>
                      <td>{row.courseId}</td>
                      <td>
                        {row.name}
                        {row.secondaryName && (
                          <span className="text-muted">
                            {' '}
                            · {row.secondaryName}
                          </span>
                        )}
                      </td>
                      <td>
                        <Badge bg={SCORE_BG[row.score] || 'secondary'}>
                          {row.score}
                        </Badge>
                      </td>
                      <td>
                        {signals
                          .filter((s) => row.signals.includes(s.name))
                          .map((s) => (
                            <OverlayTrigger
                              key={s.name}
                              placement="top"
                              overlay={
                                <Tooltip id={`${row.courseId}-${s.name}`}>
                                  {s.description}
                                </Tooltip>
                              }
                            >
                              <Badge
                                bg="light"
                                text="dark"
                                className="me-1 border"
                              >
                                {SIGNAL_LABELS[s.name] || s.name}
                              </Badge>
                            </OverlayTrigger>
                          ))}
                      </td>
                      <td className="small">
                        {placeText(row.place, countryName) || (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td>{row.layoutCount}</td>
                      <td>{row.holeCount}</td>
                      <td>
                        {row.played > 0 ? (
                          <strong title="Somebody has actually played here">
                            {row.played}
                          </strong>
                        ) : (
                          0
                        )}
                      </td>
                      <td>
                        {verdict ? (
                          <Badge bg={VERDICT_BADGE[verdict].bg}>
                            {VERDICT_BADGE[verdict].label}
                          </Badge>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td>
                        <a
                          href={searchUrl(row, countryName)}
                          target="phantom-search"
                          rel="noopener noreferrer"
                        >
                          Google
                        </a>
                        {' · '}
                        <a
                          href={mapUrl(row, countryName)}
                          target="phantom-search"
                          rel="noopener noreferrer"
                        >
                          Map
                        </a>
                      </td>
                      <td>
                        <ButtonGroup size="sm">
                          <Button
                            variant={
                              !decision ? 'secondary' : 'outline-secondary'
                            }
                            onClick={() => setDecision(row.courseId, undefined)}
                          >
                            Leave
                          </Button>
                          <Button
                            variant={
                              decision === 'HIDE' ? 'danger' : 'outline-danger'
                            }
                            onClick={() => setDecision(row.courseId, 'HIDE')}
                          >
                            Hide
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )
        }}
      </Pagination>

      {data.placeAttribution && (
        <p className="small text-muted">{data.placeAttribution}</p>
      )}

      <ApplyPanel
        decidedCount={decidedCount}
        total={candidates.length}
        dryRunSeen={dryRunSeen}
        applying={applying}
        onDryRun={() => submit(true)}
        onApply={() => submit(false)}
      />
    </>
  )
}
